import {useEffect, useRef} from 'react';
import {atom, useAtom, useAtomValue} from 'jotai';
import type {UserRole} from '@api/auth/types';
import {activeWorkspaceIdAtom} from '@api/workspaces/atoms';
import {
  THEME_MODE_STORAGE_KEY,
  applyThemeMode,
  readStoredMode,
  type ThemeMode,
} from '@lib/theme';
import {MOCK_TODAY} from '@lib/utils';

// ─── Period ───────────────────────────────────────────────────────────────

export type Period = 'day' | 'week' | 'month' | 'quarter' | 'year';

export const PERIOD_LABELS: Record<Period, string> = {
  day: 'Сегодня',
  week: 'Неделя',
  month: 'Месяц',
  quarter: 'Квартал',
  year: 'Год',
};

/** Текущий выбранный период для фильтрации данных (простой строковый — для CallsFilterBar). */
export const periodAtom = atom<Period>('week');

/**
 * Расширенное состояние периода для дашбордового PeriodPopover (раунд 4 §6).
 * Хранится как объект-дискриминированный союз; легаси-строковый `periodAtom`
 * остаётся отдельно для совместимости с другими фичами.
 */
export type PeriodPopoverState =
  | {
      kind: 'day';
      preset?: 7 | 30 | 90 | 365;
      customStart?: string;
      customEnd?: string;
      /** Точные границы внешнего отчёта; сохраняют timezone исходной публикации. */
      exactRange?: {dateFrom: string; dateTo: string};
    }
  | {kind: 'week'; current: boolean}
  | {kind: 'month'; current: boolean; year: number; monthIdx: number}
  | {kind: 'quarter'; current: boolean; year: number; quarter: 1 | 2 | 3 | 4}
  | {kind: 'year'; current: boolean; year: number};

/** Дефолт периода — «Эта неделя» (с понедельника). Единый для большинства
 *  страниц; используется и как начальное значение атома, и точкой сброса при
 *  навигации (см. AppLayout). */
export const DEFAULT_PERIOD_STATE: PeriodPopoverState = {
  kind: 'week',
  current: true,
};

export const periodStateAtom = atom<PeriodPopoverState>(DEFAULT_PERIOD_STATE);

/** Ключ страницы, загрузившей текущее значение `periodStateAtom`: только она
 *  сохраняет его под своим ключом (см. `usePagePeriod`). */
const periodOwnerAtom = atom<string | null>(null);

// ─── Per-page period persistence (sessionStorage) ────────────────────────

const PERIOD_STORAGE_PREFIX = 'colver-rop:period:';

/** Возвращает сохранённый период из sessionStorage или null, если нет сохранённого. */
const readPagePeriod = (pageKey: string): PeriodPopoverState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PERIOD_STORAGE_PREFIX + pageKey);
    if (!raw) return null;
    return JSON.parse(raw) as PeriodPopoverState;
  } catch {
    return null;
  }
};

const savePagePeriod = (pageKey: string, state: PeriodPopoverState): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      PERIOD_STORAGE_PREFIX + pageKey,
      JSON.stringify(state),
    );
  } catch {
    // ignore
  }
};

/** Стирает сохранённые периоды всех страниц — при смене воркспейса период
 *  сбрасывается на дефолт везде, а не только на текущей странице. */
const clearAllPagePeriods = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(PERIOD_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
};

/** «Сегодня» — аналог PERIOD_TODAY из pages/dashboard/model/period.ts,
 *  но без FSD cross-import из shared в pages. */
const _d = new Date(MOCK_TODAY);
const periodToday = new Date(_d.getFullYear(), _d.getMonth(), _d.getDate());

/** Дефолт периода по маршруту — используется как фолбэк, когда sessionStorage
 *  пуст (первый визит / после F5). Большинству страниц подходит «Неделя»;
 *  «Обработанные» исторически открываются на «Этот месяц». */
const PERIOD_DEFAULT_FOR_PATH: Record<string, PeriodPopoverState> = {
  '/processed': {
    kind: 'month',
    current: true,
    year: periodToday.getFullYear(),
    monthIdx: periodToday.getMonth(),
  },
};

/**
 * Синхронизирует период с sessionStorage по ключу страницы.
 * Загружает период из sessionStorage **синхронно** при монтировании (до первого
 * рендера с чужим значением), сохраняет при каждом изменении.
 * sessionStorage очищается при F5 → дефолт «Неделя».
 * Смена активного воркспейса тоже сбрасывает период на дефолт (данные другой
 * компании — новый отсчёт).
 *
 * Использование: `usePagePeriod('dashboard')` в корне страницы.
 */

const defaultForPageKey = (pageKey: string): PeriodPopoverState =>
  PERIOD_DEFAULT_FOR_PATH[`/${pageKey}`] ?? DEFAULT_PERIOD_STATE;

/** Инициализация периода страницы; initialState и persist=false дают временный фильтр отчёта без перезаписи сохранённого выбора. */
export const usePagePeriod = (
  pageKey: string,
  {
    initialState,
    persist = true,
  }: {initialState?: PeriodPopoverState; persist?: boolean} = {},
): void => {
  const [state, setState] = useAtom(periodStateAtom);
  const [legacy, setLegacy] = useAtom(periodAtom);
  const [owner, setOwner] = useAtom(periodOwnerAtom);
  const workspaceId = useAtomValue(activeWorkspaceIdAtom);
  const pageKeyRef = useRef(pageKey);
  const workspaceIdRef = useRef(workspaceId);
  const initRef = useRef(false);

  // Смена воркспейса (но не первичное определение активного) → период на дефолт.
  const workspaceSwitched =
    workspaceIdRef.current !== null && workspaceIdRef.current !== workspaceId;

  // Синхронная инициализация: если pageKey изменился ИЛИ первый рендер —
  // загружаем из sessionStorage (или дефолт для маршрута) и обновляем атом
  // ДО отрисовки дочерних элементов. React 19 батчит render-phase setState
  // с текущим рендером — дочерние компоненты видят актуальное значение.
  if (!initRef.current || pageKeyRef.current !== pageKey || workspaceSwitched) {
    if (workspaceSwitched) clearAllPagePeriods();
    pageKeyRef.current = pageKey;
    workspaceIdRef.current = workspaceId;
    initRef.current = true;
    setOwner(pageKey);
    const loaded =
      initialState ??
      (!workspaceSwitched && persist ? readPagePeriod(pageKey) : null) ??
      defaultForPageKey(pageKey);
    if (
      loaded.kind !== state.kind ||
      JSON.stringify(loaded) !== JSON.stringify(state)
    ) {
      setState(loaded);
      setLegacy(loaded.kind);
      if (persist) savePagePeriod(pageKey, loaded);
    }
  }

  // Каждое изменение периода → сохранить в sessionStorage, но только «свой».
  // Навигация идёт в startTransition: уходящая страница ещё смонтирована, когда
  // новая уже записала в атом свой период, — без проверки владельца она
  // сохранила бы чужой период под своим ключом и периоды страниц «склеивались».
  useEffect(() => {
    if (persist && owner === pageKey) savePagePeriod(pageKey, state);
    if (legacy !== state.kind) setLegacy(state.kind);
  }, [pageKey, owner, state, legacy, setLegacy, persist]);
};

// ─── Role ─────────────────────────────────────────────────────────────────

/** Текущая роль пользователя. В dev переключается через RoleSwitcher. */
export const roleAtom = atom<UserRole>('rop');

// ─── UI: Навигация / Панели ───────────────────────────────────────────────

/** Открыта ли панель уведомлений. */
export const notificationsOpenAtom = atom(false);

// `unreadCountAtom` удалён — счётчик теперь вычисляется derived-атомом
// из `notificationsAtom` (см. ниже). Так и сайдбар, и панель работают
// от одного источника правды.

// ─── Notifications shared store (раунд 11 §2) ────────────────────────────

export type NotificationKind =
  | 'priority-1'
  | 'alert'
  | 'digest'
  | 'practice'
  | 'achievement'
  | 'report'
  | 'support'
  | 'task';

export interface NotificationManager {
  initials: string;
  name: string;
}

export interface NotificationItem {
  /** Счётчики демонстрационного аналитического отчёта. */
  reportCounts?: {negative: number; positive: number};
  id: string;
  type: NotificationKind;
  unread: boolean;
  time: string;
  title: string;
  desc: string;
  manager?: NotificationManager;
  callId?: number;
  taskId?: string;
}

const INITIAL_NOTIFICATIONS: NotificationItem[] = [
  {
    id: 'NF-task',
    type: 'task',
    unread: true,
    time: '5 мин назад',
    title: 'Вам назначена задача',
    desc: 'Проверить работу с возражениями клиента.',
    taskId: 'T-01',
  },
  {
    id: 'NF-weekly',
    reportCounts: {negative: 2, positive: 2},
    type: 'report',
    unread: true,
    time: '5 мин назад',
    title: 'Недельный отчёт готов',
    desc: 'С негативной динамикой: 2\nС позитивной динамикой: 2',
  },
  {
    id: 'NF1',
    type: 'priority-1',
    unread: true,
    time: '5 мин назад',
    title: 'Возражение по цене не отработано',
    desc: 'Менеджер пропустил ключевые шаги скрипта на этапе работы с ценой.',
    manager: {initials: 'СА', name: 'Сидоров А.К.'},
    callId: 1841,
  },
  {
    id: 'NF2',
    type: 'priority-1',
    unread: true,
    time: '18 мин назад',
    title: 'Не зафиксирован следующий шаг',
    desc: 'Звонок завершён без договорённости о следующем действии.',
    manager: {initials: 'КМ', name: 'Козлов М.О.'},
    callId: 1842,
  },
  {
    id: 'NF3',
    type: 'digest',
    unread: false,
    time: '30 мин назад',
    title: 'Дайджест: 2 новых звонка в очереди',
    desc: 'Приоритет 2 — звонки #1843 и #1844 ожидают разбора.',
  },
  {
    id: 'NF4',
    type: 'alert',
    unread: true,
    time: '45 мин назад',
    title: 'Снижение конверсии',
    desc: 'Конверсия отдела упала на 4% за последние 3 дня.',
  },
  {
    id: 'NF5',
    type: 'priority-1',
    unread: false,
    time: '1 ч назад',
    title: 'Скрипт не соблюдён',
    desc: 'Менеджер отклонился от структуры звонка на этапе выявления потребности.',
    manager: {initials: 'ПИ', name: 'Петров И.С.'},
    callId: 1839,
  },
  {
    id: 'NF6',
    type: 'digest',
    unread: false,
    time: '1 ч 30 мин назад',
    title: 'Дайджест: 3 звонка обработано за час',
    desc: 'Обработано: обратная связь ×2, разбор назначен ×1.',
  },
  {
    id: 'NF7',
    type: 'practice',
    unread: false,
    time: '2 ч назад',
    title: 'Найдена лучшая практика',
    desc: 'Анна Л. показала отличную работу с возражением «дорого» — 9.4 балла.',
    manager: {initials: 'АЛ', name: 'Анна Л.'},
    callId: 1820,
  },
  {
    id: 'NF8',
    type: 'achievement',
    unread: false,
    time: '3 ч назад',
    title: 'План выполнен',
    desc: 'Мария П. выполнила недельный план на 105%.',
    manager: {initials: 'МП', name: 'Мария П.'},
  },
  {
    id: 'NF9',
    type: 'achievement',
    unread: false,
    time: '5 ч назад',
    title: 'Задача закрыта',
    desc: 'Иван С. закрыл задачу «Разбор звонка с ООО Техно».',
    manager: {initials: 'ИС', name: 'Иван С.'},
  },
  {
    id: 'NF10',
    type: 'report',
    unread: false,
    time: 'вчера',
    title: 'Еженедельный отчёт',
    desc: 'Отчёт за неделю сформирован и готов к просмотру.',
  },
];

/** Источник истины для уведомлений. Сайдбар и панель читают отсюда. */
export const notificationsAtom = atom<NotificationItem[]>(
  INITIAL_NOTIFICATIONS,
);

/** Derived: количество непрочитанных уведомлений. */
export const unreadCountAtom = atom(
  (get) => get(notificationsAtom).filter((n) => n.unread).length,
);

/** Открыта ли AI-панель. */
export const aiPanelOpenAtom = atom(false);

/** Свёрнута ли AI-панель в плавающую кнопку. */
export const aiPanelCollapsedAtom = atom(false);

/** Идёт ли сейчас генерация ответа AI. Живёт в jotai (а не в local state) —
 *  чтобы «мышление» переживало сворачивание панели (collapse), см. AiPanel. */
export const aiLoadingAtom = atom(false);

/** Активная вкладка очереди дашборда. В jotai — чтобы из уведомления
 *  «Управленческие решения» можно было открыть нужную вкладку. */
export const dashQueueTabAtom = atom<'urgent' | 'management' | 'practices'>(
  'urgent',
);

/**
 * Таб типа коммуникации в секции «Аналитика за период»: все, звонки, очные или
 * онлайн-встречи (значения встреч — `metadata.channel`).
 */
export type AnalyticsChannelFilter =
  | 'all'
  | 'phone'
  | 'offline-meeting'
  | 'online-meeting';

/** Фильтр типа коммуникации в секции «Аналитика за период». */
export const analyticsChannelFilterAtom = atom<AnalyticsChannelFilter>('all');

/**
 * Текущая ширина контентной области (между sidebar и AI-панелью).
 * Заполняется через ResizeObserver в AppLayout. Используется для решения
 * push vs overlay режима AI-панели: на широком экране (≥800px свободного
 * места рядом с открытой панелью) — push, иначе — overlay.
 */
export const contentWidthAtom = atom<number>(0);

/**
 * Активен ли push-режим AI-панели (сжатие основного контента вместо overlay).
 * Управляется в `AppLayout` на основе ширины viewport, читается Topbar'ом
 * и `AiPanel` (чтобы не рисовать overlay в push-режиме).
 */
export const aiPushModeAtom = atom<boolean>(false);

// ─── Cookie consent (раунд 25 §1) ────────────────────────────────────────

// v2: в баннер добавлено предупреждение о сборе метрик (спека 19 бэка) —
// новый ключ, чтобы обновлённый текст один раз увидели все.
const COOKIE_CONSENT_KEY = 'colver-rop:cookies-accepted:v2';

const loadCookieConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_KEY) === '1';
  } catch {
    return false;
  }
};

const baseCookieConsentAtom = atom<boolean>(loadCookieConsent());

/** Дал ли пользователь согласие на cookies. Persisted в localStorage. */
export const cookieConsentAtom = atom(
  (get) => get(baseCookieConsentAtom),
  (_get, set, next: boolean) => {
    set(baseCookieConsentAtom, next);
    if (typeof window !== 'undefined') {
      try {
        if (next) window.localStorage.setItem(COOKIE_CONSENT_KEY, '1');
        else window.localStorage.removeItem(COOKIE_CONSENT_KEY);
      } catch {
        // ignore
      }
    }
  },
);

// ─── Theme mode (личный выбор: light / dark / system) ────────────────────

const baseThemeModeAtom = atom<ThemeMode>(readStoredMode());

/**
 * Личный режим оформления (light/dark/system). Persisted в localStorage и сразу
 * применяет класс `.dark` на <html>. Палитра/радиус/шрифты задаются темой
 * воркспейса отдельно (см. `@lib/theme` + ThemeController).
 */
export const themeModeAtom = atom(
  (get) => get(baseThemeModeAtom),
  (_get, set, next: ThemeMode) => {
    set(baseThemeModeAtom, next);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
      } catch {
        // ignore quota / disabled storage
      }
    }
    applyThemeMode(next);
  },
);

/** Demo: режим «нет интернета» (раунд 16 §3). Включается из меню профиля. */
export const offlineModeAtom = atom(false);

/** Скрыт ли красный баннер «Нет соединения» (баннер прячется, режим остаётся). */
export const offlineBannerHiddenAtom = atom(false);

// ─── Notifications connection state (раунд 14) ───────────────────────────
// Хранится в localStorage, чтобы выживать перезагрузку.

export interface NotificationsConnection {
  telegram: boolean;
  max: boolean;
}

const CONNECTION_STORAGE_KEY = 'colver-rop:notifications-connection';

/** Ни один канал не подключён. */
const NO_CONNECTION: NotificationsConnection = {telegram: false, max: false};

const loadConnection = (): NotificationsConnection => {
  if (typeof window === 'undefined') return {...NO_CONNECTION};
  try {
    const raw = window.localStorage.getItem(CONNECTION_STORAGE_KEY);
    if (!raw) return {...NO_CONNECTION};
    const parsed = JSON.parse(raw) as Partial<NotificationsConnection>;
    // `max` появился позже telegram — в старом localStorage его нет.
    return {telegram: !!parsed.telegram, max: !!parsed.max};
  } catch {
    return {...NO_CONNECTION};
  }
};

const baseConnectionAtom = atom<NotificationsConnection>(loadConnection());

/** Состояние подключённых каналов уведомлений. Read/write пишет в localStorage. */
export const notificationsConnectionAtom = atom(
  (get) => get(baseConnectionAtom),
  (_get, set, next: NotificationsConnection) => {
    set(baseConnectionAtom, next);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(
          CONNECTION_STORAGE_KEY,
          JSON.stringify(next),
        );
      } catch {
        // ignore quota / disabled storage
      }
    }
  },
);

/** Контекст текущего AI-чата (например, привязанный звонок). */
export const aiContextAtom = atom<{communicationId?: string} | null>(null);

/** Сообщения текущей AI-сессии (in-memory; не переживает reload — это by design). */
export interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const aiMessagesAtom = atom<AiMessage[]>([]);

/** Есть ли непрочитанный ответ ИИ (для индикатора на свёрнутой кнопке). */
export const aiHasUnreadReplyAtom = atom(false);

// ─── Песочница: индикаторы незавершённой работы во вкладках ───────────────
// Вкладки песочницы (`/sandbox/plans`, `/sandbox/communications`) пишут сюда
// флаг «грязноты», а шапка `SandboxPage` рисует точку на нужном табе — чтобы
// было понятно: там остались наработки, можно вернуться.

/** В редакторе планов есть несохранённые правки выбранного плана. */
export const adminPlansDirtyAtom = atom(false);

/** В песочнице коммуникаций есть незапущенные наработки (файл/текст/метаданные). */
export const adminSandboxDirtyAtom = atom(false);

// ─── Калls page: column widths (раунд 30) ────────────────────────────────

const CALLS_COLS_KEY = 'colver-rop:calls-column-widths';

const loadCallsColumnWidths = (): Record<string, number> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CALLS_COLS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch {
    return {};
  }
};

const baseCallsColumnWidthsAtom = atom<Record<string, number>>(
  loadCallsColumnWidths(),
);

/**
 * Persisted-ширины колонок страницы «Звонки». Ключ — идентификатор колонки
 * (например `tg.manager`, `nt.reason`), значение — px. Сохраняется в localStorage,
 * чтобы переключение между вкладками Целевые/Нецелевые не сбрасывало настройки.
 */
export const callsColumnWidthsAtom = atom(
  (get) => get(baseCallsColumnWidthsAtom),
  (_get, set, next: Record<string, number>) => {
    set(baseCallsColumnWidthsAtom, next);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(CALLS_COLS_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
    }
  },
);

// ─── KPI-планы дашборда (persisted в localStorage) ────────────────────────

const KPI_CALLS_PLAN_KEY = 'colver-rop:kpi-calls-monthly-plan';
const KPI_SCORE_TARGET_KEY = 'colver-rop:kpi-avg-score-target';

const loadNumber = (key: string, fallback: number): number => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
};

const baseKpiCallsMonthlyPlanAtom = atom<number>(
  loadNumber(KPI_CALLS_PLAN_KEY, 0),
);

/** Месячный план звонков (пользователь вводит вручную через ⚙️). 0 = не задан. */
export const kpiCallsMonthlyPlanAtom = atom(
  (get) => get(baseKpiCallsMonthlyPlanAtom),
  (_get, set, next: number) => {
    set(baseKpiCallsMonthlyPlanAtom, next);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(KPI_CALLS_PLAN_KEY, String(next));
      } catch {
        // ignore
      }
    }
  },
);

const baseKpiAvgScoreTargetAtom = atom<number>(
  loadNumber(KPI_SCORE_TARGET_KEY, 0),
);

/** Целевая средняя оценка звонков (0–10, не зависит от периода). 0 = не задана. */
export const kpiAvgScoreTargetAtom = atom(
  (get) => get(baseKpiAvgScoreTargetAtom),
  (_get, set, next: number) => {
    set(baseKpiAvgScoreTargetAtom, next);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(KPI_SCORE_TARGET_KEY, String(next));
      } catch {
        // ignore
      }
    }
  },
);

// ─── UI: Звонки ───────────────────────────────────────────────────────────

/** ID выбранного звонка для детального просмотра. */
export const selectedCallIdAtom = atom<string | null>(null);

/** Открыта ли модаль детального просмотра звонка. */
export const callDetailOpenAtom = atom(false);

// ─── UI: Задачи ───────────────────────────────────────────────────────────

/** ID выбранного менеджера для просмотра профиля. */
export const selectedManagerIdAtom = atom<string | null>(null);

/** Открыта ли модаль профиля менеджера. */
export const managerDetailOpenAtom = atom(false);

/** Открыта ли модаль создания задачи. */
export const taskCreateOpenAtom = atom(false);

/** ID задачи для детального просмотра. */
export const selectedTaskIdAtom = atom<string | null>(null);

/** Открыта ли модаль детального просмотра задачи. */
export const taskDetailOpenAtom = atom(false);

/** Множество id очереди, отмеченных как решённые (исчезают из таблицы). */
export const resolvedQueueIdsAtom = atom<Set<string>>(new Set<string>());

/** Запись в «Обработанных» — пополняется по двум путям (mark resolved / close task). */
export interface ProcessedRecord {
  id: string;
  /** ISO datetime (используется для сортировки и форматирования). */
  at: string;
  problemTitle: string;
  /** Ссылка на исходную запись (id звонка или встречи) — общий канал resolve. */
  callId: string;
  managerShort: string;
  action: string;
  /** Откуда пришла запись — для диагностики. */
  source: 'resolve' | 'task_closed';
}

export const processedRecordsAtom = atom<ProcessedRecord[]>([]);

/** ID звонка для дашбордовой модалки карточки звонка (§3 раунда 4). */
export const dashCallIdAtom = atom<string | null>(null);

/** Открыта ли дашбордовая модалка звонка. */
export const dashCallOpenAtom = atom(false);

/**
 * Контекст очереди для модалки звонка: рекомендация нейроаналитика, теги,
 * заголовок проблемы и клиент. Задаётся при открытии из очереди дашборда —
 * раньше эти данные модалка доставала из mock-массивов, из-за чего на реальном
 * бэке выглядела «замоканной/пустой». Сбрасывается в null при открытии не из очереди.
 */
export interface DashCallContext {
  /** id элемента очереди (для отметки «решено» из модалки). */
  id?: string;
  recommendation?: string;
  tags?: string[];
  /** Ред-флаг на звонке (ТЗ 3.4): тег рисуется визуально отличным от негатива. */
  isRedFlag?: boolean;
  problemTitle?: string;
  clientShort?: string;
  managerShort?: string;
}

export const dashCallContextAtom = atom<DashCallContext | null>(null);

/** Префилл для модали создания задачи (из CallDetailModal / `+ Добавить` в колонке канбана). */
export interface TaskCreatePrefill {
  managerId?: string;
  managerName?: string;
  communicationId?: string;
  /** Сохранённый аналитический инсайт — задача создаётся через его отдельный endpoint. */
  analyticsInsightSid?: string;
  /** Ссылка на встречу (вместо звонка) для префилла из MeetingDetailModal. */
  meetingId?: string;
  title?: string;
  /** Предзаполненное описание задачи. */
  text?: string;
  /** Предзаполненный приоритет. */
  priority?: 'high' | 'medium' | 'low';
  /** Статус по умолчанию — для `+ Добавить` в колонке kanban. */
  initialStatus?:
    | 'planned'
    | 'queued'
    | 'in_progress'
    | 'paused'
    | 'partial'
    | 'done';
}
export const taskCreatePrefillAtom = atom<TaskCreatePrefill | null>(null);

// ─── UI: Встречи ──────────────────────────────────────────────────────────

/** ID выбранной встречи для детального просмотра. */
export const meetingDetailIdAtom = atom<string | null>(null);

/** Открыта ли модалка детального просмотра встречи. */
export const meetingDetailOpenAtom = atom(false);
// ─── Notification settings (настройки уведомлений) ─────────────────────────

/** Открыто ли модальное окно настроек уведомлений. */
export const notificationSettingsOpenAtom = atom(false);

/** Доступные периодичности отправки уведомлений. */
export type NotificationFrequency =
  | 'never'
  | 'instant'
  | '30min'
  | '1hour'
  | '4hours'
  | '1day';

/** Настройки одного раздела уведомлений (per-канал). */
export interface NotificationSectionSettings {
  frequency: NotificationFrequency;
  telegram: boolean;
  max: boolean;
}

/** Глобальные настройки уведомлений по разделам. */
export interface NotificationSettings {
  priority1: NotificationSectionSettings;
  priority2: NotificationSectionSettings;
}

const SETTINGS_STORAGE_KEY = 'colver-rop:notification-settings';

const DEFAULT_SECTION: NotificationSectionSettings = {
  frequency: 'never',
  telegram: false,
  max: false,
};

/** Дефолтные настройки уведомлений (все частоты = 'never', каналы выключены). */
export const DEFAULT_SETTINGS: NotificationSettings = {
  priority1: {...DEFAULT_SECTION},
  priority2: {...DEFAULT_SECTION},
};

/** Достраивает секцию до актуальной формы: в старом localStorage нет `max`. */
const normalizeSection = (
  raw: Partial<NotificationSectionSettings> | undefined,
): NotificationSectionSettings => ({
  ...DEFAULT_SECTION,
  ...raw,
  max: !!raw?.max,
});

const loadSettings = (): NotificationSettings => {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<NotificationSettings>;
    return {
      priority1: normalizeSection(parsed.priority1),
      priority2: normalizeSection(parsed.priority2),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const baseSettingsAtom = atom<NotificationSettings>(loadSettings());

/** Настройки уведомлений. Read/write пишет в localStorage. */
export const notificationSettingsAtom = atom(
  (get) => get(baseSettingsAtom),
  (_get, set, next: NotificationSettings) => {
    set(baseSettingsAtom, next);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
    }
  },
);
